import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { REWARD_CONSENT_TEXT, type CampaignOfferView, type StoreOfferKind, type StoreOffersStatus, type StoreOffersView } from "@townsy/contracts";
import { isApiError } from "../../../api/client";
import { useBootstrapQuery } from "../../../api/queries";
import { invalidateCampaigns } from "../../../api/rewardsQueries";
import { fetchStoreOffers, newStoreOffersKey, postRemoveOffer, putStoreOffers, storeOffersKeys, useStoreOffersQuery } from "../../../api/storeOffers";
import { formatCoins } from "../../../lib/wallet";
import { earlierPartnerOfferNote } from "../../../rewards/copy";
import {
  FREE_OFFERS_LINE,
  OFFER_REVIEW_WORDS,
  REMOVE_OFFER_CONFIRM,
  addOffer,
  afterRemoval,
  canAddOffer,
  changeKind,
  editOffer,
  nextSteps,
  offerCount,
  offersFormFromReuse,
  offersFormFromView,
  offersLoadErrorMessage,
  offersSaveErrorMessage,
  offersSavedMessage,
  offersStatusCopy,
  partnerOffersOf,
  primaryAction,
  removeOffer,
  reuseChoice,
  reuseNeedsWebsiteLine,
  reuseQuestion,
  reuseRewardsLine,
  validateOffersForm,
  type OfferFieldErrors,
  type OfferForm,
  type OffersForm,
  type OffersStatusCopy,
  type OffersValidation,
  type PrimaryAction,
} from "../../../rewards/storeOffersModel";
import { useTownStore, type OfferSetupRequestUi } from "../../../state/store";
import { Dialog } from "../../Dialog";

/**
 * "Add an offer" / "Edit offers" (ADR-0027, founder request 2026-10-20: "Also can we make it easier to "add offers" as a
 * building owner?"): ONE screen for the current owner of a store, in plain words, with today's defaults filled in and a
 * live summary: what visitors do (visit the store's website, or answer a question about the store), the title and short
 * instructions, the reward, the consent, and ONE button that saves everything and sends it to review. The offers then
 * start by themselves once they pass review (the server decides; no Activate button).
 *
 * ADR-0029 (founder decision 2026-10-20): offers are FREE for the building owner: no price, no budget, no payment; Townsy
 * gives the coins. ADR-0030: no per-store daily coin limit, and "Re-use your offers" fills this screen from the owner's
 * own earlier offers (a store they lost, or offers they paused). Partner-confirmed tasks (they need partner keys) and
 * reports stay in the advertiser tools.
 */
export function OfferSetupHost() {
  const request = useTownStore((s) => s.offerSetup);
  if (!request) return null;
  return <OfferSetupLoader key={request.id} propertyId={request.propertyId} focus={request.focus} />;
}

function OfferSetupLoader({ propertyId, focus }: { propertyId: string; focus: OfferSetupRequestUi["focus"] }) {
  const close = useTownStore((s) => s.closeOfferSetup);
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const q = useStoreOffersQuery(signedIn ? propertyId : null);
  if (boot.isPending || (signedIn && q.isPending)) {
    return (
      <Dialog title="Your offers" onClose={close} className="ss-dialog-small">
        <p className="muted" role="status" style={{ margin: 0 }} data-testid="offer-setup-loading">
          Loading your offers…
        </p>
      </Dialog>
    );
  }
  if (!signedIn || q.error || !q.data) {
    const message = !signedIn ? "Sign in with the account that owns this store to add offers." : offersLoadErrorMessage(q.error);
    const retry = signedIn && !(isApiError(q.error) && [401, 403, 404, 422].includes(q.error.status));
    return (
      <Dialog title="Your offers" onClose={close} className="ss-dialog-small">
        <div className="stack" data-testid="offer-setup-unavailable">
          <p className="notice notice-info" role="status" style={{ margin: 0 }}>
            {message}
          </p>
          <div className="row">
            {retry ? (
              <button type="button" className="btn btn-primary" onClick={() => void q.refetch()}>
                Try again
              </button>
            ) : null}
            {!signedIn ? (
              <Link className="btn btn-primary" to="/account" onClick={close}>
                Sign in
              </Link>
            ) : null}
            <button type="button" className="btn" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </Dialog>
    );
  }
  return <OfferSetupEditor view={q.data} onClose={close} focus={focus} partnerOffersOn={boot.data?.features?.partnerOffersEnabled === true} />;
}

function OfferSetupEditor({ view, onClose, focus, partnerOffersOn }: { view: StoreOffersView; onClose(): void; focus: OfferSetupRequestUi["focus"]; partnerOffersOn: boolean }) {
  const qc = useQueryClient();
  const openStoreSetup = useTownStore((s) => s.openStoreSetup);
  const pushToast = useTownStore((s) => s.pushToast);
  // ADR-0030: opened from "Re-use your offers" (the panel, the notice): the screen starts filled in from them.
  const [form, setForm] = useState<OffersForm>(() => (focus === "reuse" ? offersFormFromReuse(view) : offersFormFromView(view)));
  // The screen can open on cached data from before the store went live (e.g. right after "Re-use this design"): when the
  // re-usable offers arrive with the fresh data and nothing has been typed yet, fill the screen in from them once.
  useEffect(() => {
    if (focus !== "reuse" || form.reusedFrom || form.offers.length > 0) return;
    const choice = reuseChoice(view);
    if (!choice?.enabled) return;
    setForm(offersFormFromReuse(view));
  }, [focus, view, form.reusedFrom, form.offers.length]);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);
  const v = validateOffersForm(form, view);
  const action = primaryAction(form, view, v);
  const dirty = v.changed || form.consent;

  // A reviewer changed the status while the screen is open: say so once.
  const lastState = useRef(view.status.state);
  useEffect(() => {
    const before = lastState.current;
    lastState.current = view.status.state;
    if (before !== view.status.state && view.status.state === "live") pushToast(`Your offers at ${view.address} are live.`, "success");
  }, [view.status.state, view.address, pushToast]);

  const update = (next: OffersForm) => {
    setForm(next);
    setResult(null);
    setConfirmClose(false);
    keyRef.current = null; // a different request needs a different Idempotency-Key
  };

  const submit = async () => {
    setShowErrors(true);
    if (saving || !action.enabled) return;
    if (!v.ok) {
      setResult({ tone: "error", text: "Check the highlighted fields, then try again." });
      return;
    }
    if (!v.request) return;
    keyRef.current ??= newStoreOffersKey();
    setSaving(true);
    setResult(null);
    try {
      const res = await putStoreOffers(view.propertyId, v.request, keyRef.current);
      keyRef.current = null;
      qc.setQueryData(storeOffersKeys.one(view.propertyId), res.offers);
      invalidateCampaigns(qc);
      setForm(offersFormFromView(res.offers));
      setShowErrors(false);
      setResult({ tone: "success", text: offersSavedMessage(res.saved, res.offers.status) });
    } catch (err) {
      setResult({ tone: "error", text: offersSaveErrorMessage(err) });
      // A final refusal is answered the same for the same key: the next press is a new request.
      if (!(isApiError(err) && err.retryable)) keyRef.current = null;
    } finally {
      setSaving(false);
    }
  };

  /** "Remove this offer" (a saved one): retired at once on the server. */
  const removeSaved = async (offerId: string) => {
    if (!view.campaign || removing) return;
    setRemoving(offerId);
    setResult(null);
    try {
      await postRemoveOffer(view.campaign.campaignId, offerId);
      const fresh = await fetchStoreOffers(view.propertyId);
      qc.setQueryData(storeOffersKeys.one(view.propertyId), fresh);
      invalidateCampaigns(qc);
      setForm((f) => afterRemoval(f, fresh));
      keyRef.current = null;
      setConfirmRemove(null);
      setResult({ tone: "success", text: "Offer removed. Visitors can't start it any more." });
    } catch (err) {
      setResult({ tone: "error", text: offersSaveErrorMessage(err) });
    } finally {
      setRemoving(null);
    }
  };

  const requestClose = () => {
    if (saving || removing) return;
    if (dirty && !confirmClose) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  return (
    <OfferSetupScreen
      view={view}
      form={form}
      validation={v}
      action={action}
      showErrors={showErrors}
      saving={saving}
      result={result}
      confirmClose={confirmClose}
      onForm={update}
      onPrimary={() => void submit()}
      onSetUpStore={() => {
        onClose();
        openStoreSetup(view.propertyId, "panel");
      }}
      onClose={requestClose}
      onDiscard={onClose}
      onKeepEditing={() => setConfirmClose(false)}
      confirmRemoveId={confirmRemove}
      removingId={removing}
      onAskRemove={setConfirmRemove}
      onRemoveSaved={(offerId) => void removeSaved(offerId)}
      partnerOffersOn={partnerOffersOn}
    />
  );
}

// ------------------------------------------------------------------------------------------------- the screen

export interface OfferSetupScreenProps {
  view: StoreOffersView;
  form: OffersForm;
  validation: OffersValidation;
  action: PrimaryAction;
  /** Show every field's error (after the first press). */
  showErrors: boolean;
  saving: boolean;
  result: { tone: "success" | "error"; text: string } | null;
  confirmClose: boolean;
  onForm(next: OffersForm): void;
  onPrimary(): void;
  onSetUpStore(): void;
  onClose(): void;
  onDiscard(): void;
  onKeepEditing(): void;
  /** "Remove this offer": the saved offer whose confirmation is open, and the one being removed. */
  confirmRemoveId?: string | null;
  removingId?: string | null;
  onAskRemove?(offerId: string | null): void;
  onRemoveSaved?(offerId: string): void;
  /** ADR-0034 addendum: sign-up (partner-confirmed) offers run on this server (bootstrap); off = an earlier one is stopped. */
  partnerOffersOn?: boolean;
}

/** The whole screen (stateless; server-rendered in unit tests). */
export function OfferSetupScreen(p: OfferSetupScreenProps) {
  const { view, form, validation: v } = p;
  const id = useId();
  const hasOffers = Boolean(view.campaign);
  const copy = offersStatusCopy(view.status, { quickCheck: view.quickCheck });
  const partner = partnerOffersOf(view);
  const footer = (
    <div className="ss-footer">
      {p.result && !p.confirmClose ? (
        <div className={`ss-result ss-result-${p.result.tone}`} role={p.result.tone === "error" ? "alert" : "status"} data-testid="offer-setup-result">
          {p.result.text}
        </div>
      ) : p.confirmClose ? (
        <div className="ss-discard" role="alert" data-testid="offer-setup-discard">
          <span>Close without saving your changes?</span>
          <button type="button" className="btn btn-sm btn-danger" onClick={p.onDiscard} data-testid="offer-setup-discard-yes">
            Discard changes
          </button>
          <button type="button" className="btn btn-sm" onClick={p.onKeepEditing}>
            Keep editing
          </button>
        </div>
      ) : (
        <span className="ss-footer-note hint">
          {!p.action.enabled
            ? p.action.reason
            : `${view.quickCheck ? "A quick automated check or a Townsy reviewer" : "A Townsy reviewer"} checks new offers. They start by themselves once they pass. Free for you.`}
        </span>
      )}
      <div className="ss-footer-actions">
        <button type="button" className="btn" onClick={p.onClose} disabled={p.saving}>
          Close
        </button>
        {view.store ? (
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={p.onPrimary}
            disabled={p.saving || !p.action.enabled}
            title={p.action.enabled ? undefined : p.action.reason}
            data-testid="offer-setup-save"
            data-action={p.action.kind}
          >
            {p.saving ? "Saving…" : p.action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
  return (
    <Dialog
      title={
        <span className="ss-title">
          {hasOffers ? "Edit offers" : "Add an offer"}
          <span className="ss-title-address">{view.address}</span>
        </span>
      }
      onClose={p.onClose}
      wide
      className="ss-dialog os-dialog"
      describedBy={`${id}-status`}
      footer={footer}
    >
      <div className="ss os" data-testid="offer-setup" data-state={view.status.state}>
        {hasOffers ? (
          <OffersStatusBanner id={`${id}-status`} status={view.status} copy={copy} />
        ) : (
          <p className="ss-lead os-lead" id={`${id}-status`} data-testid="offer-setup-lead">
            Visitors earn coins for a clear action at your store, and Townsy gives the coins: offers are free for you. Pick one below.
          </p>
        )}
        {view.store ? <ReuseBanner view={view} form={form} disabled={p.saving || Boolean(p.removingId)} onReuse={() => p.onForm(offersFormFromReuse(view))} /> : null}
        {view.paidOffersElsewhere ? (
          <p className="notice notice-info small" style={{ margin: 0 }} data-testid="offer-setup-paid-elsewhere">
            Offers you paid for before offers became free keep running in the <Link to="/advertise?tab=tasks">advertiser tools</Link> until they are used up. This screen
            shows your free offers.
          </p>
        ) : null}

        {!view.store ? (
          <section className="ss-card-section" data-testid="offer-setup-not-live">
            <h3 className="ss-h">Set up your store first</h3>
            <p style={{ margin: 0 }}>Offers use your store's name, logo and website, so your store must be live before you add one.</p>
            {view.reuse ? (
              <p className="small" style={{ margin: 0 }} data-testid="offer-setup-reuse-later">
                Once your store is live, you can re-use your offers from {view.reuse.fromBusinessName} ({view.reuse.fromAddress}) here.
              </p>
            ) : null}
            <div>
              <button type="button" className="btn btn-primary" onClick={p.onSetUpStore} data-testid="offer-setup-set-up-store">
                Set up your store
              </button>
            </div>
          </section>
        ) : (
          <div className="ss-layout">
            <div className="ss-form">
              {form.offers.map((o, i) => (
                <OfferCard
                  key={o.key}
                  index={i}
                  offer={o}
                  view={view}
                  errors={p.showErrors ? v.offerErrors[o.key] : undefined}
                  removable={!o.offerId && form.offers.length + partner.length > 1}
                  removeSaved={o.offerId && p.onRemoveSaved ? { confirm: p.confirmRemoveId === o.offerId, busy: p.removingId === o.offerId, onAsk: (open) => p.onAskRemove?.(open ? o.offerId : null), onRemove: () => p.onRemoveSaved?.(o.offerId!) } : null}
                  disabled={p.saving || Boolean(p.removingId)}
                  onChange={(patch) => p.onForm(editOffer(form, o.key, patch))}
                  onKind={(k) => p.onForm(changeKind(form, o.key, k, view))}
                  onRemove={() => p.onForm(removeOffer(form, o.key))}
                />
              ))}
              {partner.map((o) => (
                <PartnerOfferNote
                  key={o.offerId}
                  offer={o}
                  on={p.partnerOffersOn === true}
                  removeSaved={p.onRemoveSaved ? { confirm: p.confirmRemoveId === o.offerId, busy: p.removingId === o.offerId, onAsk: (open) => p.onAskRemove?.(open ? o.offerId : null), onRemove: () => p.onRemoveSaved?.(o.offerId) } : null}
                />
              ))}
              <div className="row">
                <button type="button" className="btn" onClick={() => p.onForm(addOffer(form, view))} disabled={p.saving || !canAddOffer(form, view)} data-testid="offer-setup-add-another">
                  Add another offer ({offerCount(form, view)}/{view.limits.maxOffers})
                </button>
                {!canAddOffer(form, view) ? <span className="hint">A store can have up to {view.limits.maxOffers} offers.</span> : null}
              </div>
              <section className="ss-card-section os-consent-section" aria-labelledby={`${id}-consent`}>
                <h3 id={`${id}-consent`} className="ss-h">
                  Your consent
                </h3>
                <label className="consent-check os-consent">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => p.onForm({ ...form, consent: e.target.checked })}
                    disabled={p.saving}
                    aria-invalid={Boolean(p.showErrors && v.consentError)}
                    data-testid="offer-setup-consent"
                  />
                  <span>{REWARD_CONSENT_TEXT}</span>
                </label>
                {p.showErrors && v.consentError ? (
                  <span className="small error-text" role="alert">
                    {v.consentError}
                  </span>
                ) : (
                  <span className="hint">Needed for every new or changed offer. Changing an offer asks for it again.</span>
                )}
              </section>
            </div>
            <aside className="ss-aside" aria-label="Summary">
              <OffersSummary view={view} form={form} validation={v} />
            </aside>
          </div>
        )}

        <p className="hint ss-more" style={{ margin: 0 }} data-testid="offer-setup-reports">
          Reports for your offers are in the <Link to="/advertise?tab=tasks">advertiser tools</Link>.
        </p>
      </div>
    </Dialog>
  );
}

/**
 * "Re-use your offers" (ADR-0030): the owner's own earlier offers (a store they lost, or offers they paused), offered
 * while this store has no running offers. One press fills the screen; the owner then checks them, ticks the consent and
 * sends them for review like any new offer.
 */
function ReuseBanner({ view, form, disabled, onReuse }: { view: StoreOffersView; form: OffersForm; disabled: boolean; onReuse(): void }) {
  const choice = reuseChoice(view);
  if (!choice) return null;
  const { reuse } = choice;
  const needsWebsite = reuseNeedsWebsiteLine(choice.needWebsite);
  if (form.reusedFrom) {
    return (
      <div className="notice notice-success small os-reuse" role="status" data-testid="offer-setup-reused">
        <span>
          Filled in from your offers at {form.reusedFrom.address} ({form.reusedFrom.businessName}). Check them, tick the box below, then send them for review. They start by
          themselves once they pass.
        </span>
        {needsWebsite ? <span data-testid="offer-setup-reuse-needs-website">{needsWebsite}</span> : null}
        {reuseRewardsLine(form.reusedFrom.rewardsAdjusted) ? <span data-testid="offer-setup-reuse-rewards">{reuseRewardsLine(form.reusedFrom.rewardsAdjusted)}</span> : null}
      </div>
    );
  }
  return (
    <section className="notice notice-info os-reuse" aria-label="Re-use your offers" data-testid="offer-setup-reuse-banner">
      <strong>{reuseQuestion(reuse)}</strong>
      <ul className="os-reuse-list small">
        {reuse.offers.map((t, i) => (
          <li key={i}>
            {t.title} · {t.kind === "quiz" ? "a question" : "a website visit"} · +{formatCoins(t.rewardCoins)}
          </li>
        ))}
      </ul>
      <div className="row">
        <button type="button" className="btn btn-primary btn-sm" onClick={onReuse} disabled={disabled || !choice.enabled} title={choice.reason ?? undefined} data-testid="offer-setup-reuse">
          Re-use these offers
        </button>
        <span className="hint">{choice.reason ?? "Fills in this screen. You check them, tick the box and send them for review."}</span>
      </div>
      {choice.enabled && needsWebsite ? (
        <span className="small" data-testid="offer-setup-reuse-needs-website">
          {needsWebsite}
        </span>
      ) : null}
    </section>
  );
}

/** The one plain status (also in the store panel). Reuses the store screen's status styles. */
export function OffersStatusBanner({ id, status, copy, compact = false }: { id?: string; status: Pick<StoreOffersStatus, "state">; copy: OffersStatusCopy; compact?: boolean }) {
  return (
    <div id={id} className={`ss-status ss-status-${copy.tone}${compact ? " is-compact" : ""}`} role="status" aria-live="polite" data-testid="offer-setup-status" data-state={status.state}>
      <span className="ss-status-dot" aria-hidden="true" />
      <span className="ss-status-text">
        <strong className="ss-status-title">{copy.title}</strong>
        <span className="ss-status-detail">{copy.detail}</span>
      </span>
    </div>
  );
}

/** ADR-0030: no daily coin limit for the store; each visitor's own limits stay. */
export const NO_STORE_LIMIT_LINE = "No daily limit for your store. Each visitor can do each offer once, within their own daily coin limit.";

/** What Townsy can and cannot check, in this screen's words (docs/07 section 2: shown before the offers can start). */
export const OFFER_CAN_CHECK: Record<StoreOfferKind, string> = {
  website_visit:
    "Townsy can only confirm that a signed-in visitor opened your website from Townsy and came back after the minimum time. It can't see whether your page loaded, stayed open or was read.",
  quiz: "Townsy checks the answer on its server. A right answer is a finished question, not a website visit or a sale.",
};

const CHOICES: Array<{ kind: StoreOfferKind; title: string }> = [
  { kind: "website_visit", title: "Visit my website" },
  { kind: "quiz", title: "Answer a question about my store" },
];

function OfferCard({
  index,
  offer: o,
  view,
  errors,
  removable,
  removeSaved,
  disabled,
  onChange,
  onKind,
  onRemove,
}: {
  index: number;
  offer: OfferForm;
  view: StoreOffersView;
  errors: OfferFieldErrors | undefined;
  removable: boolean;
  /** A saved offer: "Remove this offer" with its confirmation (null: not offered). */
  removeSaved: RemoveSavedControl | null;
  disabled: boolean;
  onChange(patch: Partial<Omit<OfferForm, "key" | "offerId" | "saved" | "dirty">>): void;
  onKind(kind: StoreOfferKind): void;
  onRemove(): void;
}) {
  const id = useId();
  const e = errors ?? {};
  const host = view.store?.websiteHost ?? null;
  const reward = Number(o.rewardCoins);
  const customReward = !view.limits.rewardChoices.includes(reward);
  const err = (k: keyof OfferFieldErrors) => (e[k] ? <span className="small error-text">{e[k]}</span> : null);
  const choiceDetail = (k: StoreOfferKind) =>
    k === "website_visit"
      ? host
        ? `Visitors open ${host} from Townsy and come back after ${o.minReturnSeconds || view.defaults.website_visit.minReturnSeconds} seconds to collect their coins.`
        : "Your store has no website yet. Add one with Edit your store."
      : "Visitors read a few words about your store and answer one question here in Townsy.";
  return (
    <section className="ss-card-section os-offer" aria-labelledby={`${id}-h`} data-testid="offer-setup-offer" data-kind={o.kind}>
      <div className="os-offer-head">
        <h3 id={`${id}-h`} className="ss-h">
          {o.offerId ? `Offer ${index + 1}` : index === 0 && !view.campaign ? "Your offer" : `New offer`}
        </h3>
        <span className={`badge${o.saved?.reviewState === "approved" ? " order-badge-success" : o.saved?.reviewState === "rejected" ? " order-badge-danger" : ""}`} data-testid="offer-setup-offer-state">
          {o.saved ? (o.dirty ? "Changed: checked again after you send it" : OFFER_REVIEW_WORDS[o.saved.reviewState]) : "New"}
        </span>
        {removable ? (
          <button type="button" className="btn btn-sm btn-ghost os-remove" onClick={onRemove} disabled={disabled}>
            Remove
          </button>
        ) : null}
        {removeSaved && !removeSaved.confirm ? (
          <button type="button" className="btn btn-sm btn-ghost os-remove" onClick={() => removeSaved.onAsk(true)} disabled={disabled} data-testid="offer-setup-remove">
            Remove this offer
          </button>
        ) : null}
      </div>
      {removeSaved?.confirm ? <RemoveConfirm control={removeSaved} /> : null}

      <fieldset className="os-choices" disabled={disabled}>
        <legend>What visitors do</legend>
        <div className="os-choice-row">
          {CHOICES.map((c) => {
            const blocked = (Boolean(o.offerId) && o.kind !== c.kind) || (c.kind === "website_visit" && !host && o.kind !== "website_visit");
            return (
              <label key={c.kind} className={`os-choice${o.kind === c.kind ? " is-on" : ""}${blocked ? " is-off" : ""}`}>
                <input type="radio" name={`${id}-kind`} value={c.kind} checked={o.kind === c.kind} disabled={blocked} onChange={() => onKind(c.kind)} data-testid={`offer-setup-kind-${c.kind === "website_visit" ? "website" : "question"}`} />
                <span className="os-choice-title">{c.title}</span>
                <span className="os-choice-detail">{choiceDetail(c.kind)}</span>
              </label>
            );
          })}
        </div>
        {o.offerId ? <span className="hint">A saved offer keeps what visitors do. Add another offer for the other choice.</span> : null}
        {err("kind")}
      </fieldset>

      {o.kind === "quiz" ? <QuestionFields offer={o} errors={e} disabled={disabled} onChange={onChange} idBase={id} /> : null}

      <label className="field">
        <span>Title (visitors see it)</span>
        <input type="text" value={o.title} maxLength={120} onChange={(ev) => onChange({ title: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.title)} data-testid="offer-setup-title" />
        {err("title") ?? <span className="hint">{Array.from(o.title).length}/60</span>}
      </label>
      <label className="field">
        <span>Short instructions</span>
        <textarea rows={2} value={o.instructions} maxLength={560} onChange={(ev) => onChange({ instructions: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.instructions)} data-testid="offer-setup-instructions" />
        {err("instructions") ?? <span className="hint">{Array.from(o.instructions).length}/280</span>}
      </label>

      <fieldset className="os-reward" disabled={disabled}>
        <legend>Reward for visitors</legend>
        <div className="os-chips" role="group" aria-label="Coins per completion">
          {view.limits.rewardChoices.map((c) => (
            <button key={c} type="button" className={`os-chip${reward === c ? " is-on" : ""}`} aria-pressed={reward === c} onClick={() => onChange({ rewardCoins: String(c) })} data-testid={`offer-setup-reward-${c}`}>
              {formatCoins(c)}
            </button>
          ))}
        </div>
        {/* ADR-0034: 5 or 10 coins only; a saved offer with another amount keeps running until it is changed. */}
        {err("rewardCoins") ??
          (customReward ? (
            <span className="hint" data-testid="offer-setup-reward-earlier">
              This offer gives {formatCoins(reward)} and keeps running as it is. To change it, choose 5 or 10 coins.
            </span>
          ) : (
            <span className="hint">5 or 10 coins per completion: a reward from Townsy with no cash value.</span>
          ))}
      </fieldset>

      <p className="os-cost" data-testid="offer-setup-cost">
        Free for you: Townsy gives the coins.
      </p>

      <details className="os-more" open={Boolean(e.attemptTtlMinutes || e.dailyCompletionLimit || e.minReturnSeconds || e.maxTries)}>
        <summary data-testid="offer-setup-more">More options</summary>
        <div className="form-grid">
          <label className="field">
            <span>Time to finish (minutes)</span>
            <input type="text" inputMode="numeric" value={o.attemptTtlMinutes} onChange={(ev) => onChange({ attemptTtlMinutes: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.attemptTtlMinutes)} />
            {err("attemptTtlMinutes") ?? <span className="hint">2-60. Never past midnight UTC.</span>}
          </label>
          <label className="field">
            <span>Completions per day (optional)</span>
            <input type="text" inputMode="numeric" placeholder="No limit" value={o.dailyCompletionLimit} onChange={(ev) => onChange({ dailyCompletionLimit: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.dailyCompletionLimit)} />
            {err("dailyCompletionLimit") ?? <span className="hint">Per UTC day. Empty: no limit.</span>}
          </label>
          {o.kind === "website_visit" ? (
            <label className="field">
              <span>Minimum seconds on your website</span>
              <input type="text" inputMode="numeric" value={o.minReturnSeconds} onChange={(ev) => onChange({ minReturnSeconds: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.minReturnSeconds)} data-testid="offer-setup-min-seconds" />
              {err("minReturnSeconds") ?? <span className="hint">15-600, measured by Townsy from the click. It doesn't show the page was read.</span>}
            </label>
          ) : (
            <label className="field">
              <span>Tries per visitor</span>
              <select value={o.maxTries} onChange={(ev) => onChange({ maxTries: ev.target.value })} disabled={disabled}>
                {["1", "2", "3"].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {err("maxTries") ?? <span className="hint">Out of tries: the question closes for that visitor, with no coins.</span>}
            </label>
          )}
        </div>
      </details>

      <p className="hint os-verify" data-testid="offer-setup-can-check">
        <strong>What Townsy can check:</strong> {OFFER_CAN_CHECK[o.kind]}
      </p>
      {err("terms")}
    </section>
  );
}

function QuestionFields({
  offer: o,
  errors: e,
  disabled,
  onChange,
  idBase,
}: {
  offer: OfferForm;
  errors: OfferFieldErrors;
  disabled: boolean;
  onChange(patch: Partial<Omit<OfferForm, "key" | "offerId" | "saved" | "dirty">>): void;
  idBase: string;
}) {
  const choices = o.choices;
  return (
    <div className="stack os-question" style={{ gap: 10 }}>
      <label className="field">
        <span>What visitors read first (the right answer should be in it)</span>
        <textarea rows={2} value={o.explanation} maxLength={560} onChange={(ev) => onChange({ explanation: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.explanation)} data-testid="offer-setup-explanation" />
        {e.explanation ? <span className="small error-text">{e.explanation}</span> : <span className="hint">For example what you sell, since when, what makes you special.</span>}
      </label>
      <label className="field">
        <span>The question</span>
        <input type="text" value={o.question} maxLength={400} placeholder="What do we sell?" onChange={(ev) => onChange({ question: ev.target.value })} disabled={disabled} aria-invalid={Boolean(e.question)} data-testid="offer-setup-question" />
        {e.question ? <span className="small error-text">{e.question}</span> : null}
      </label>
      <fieldset className="choice-group os-answers" disabled={disabled}>
        <legend className="small">Answers: 2 to 4. Mark the right one.</legend>
        {choices.map((c, i) => (
          <div key={i} className="row quiz-answer-row">
            <input
              type="radio"
              name={`${idBase}-right`}
              checked={o.correctChoice === i}
              onChange={() => onChange({ correctChoice: i })}
              aria-label={`Answer ${i + 1} is the right one`}
              data-testid={`offer-setup-right-${i}`}
            />
            <input
              type="text"
              maxLength={160}
              value={c}
              onChange={(ev) => onChange({ choices: choices.map((x, j) => (j === i ? ev.target.value : x)) })}
              aria-label={`Answer ${i + 1}`}
              data-testid={`offer-setup-answer-${i}`}
              style={{ flex: 1, minWidth: 0 }}
            />
            {choices.length > 2 ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                aria-label={`Remove answer ${i + 1}`}
                onClick={() =>
                  onChange({
                    choices: choices.filter((_, j) => j !== i),
                    correctChoice: o.correctChoice === null ? null : o.correctChoice === i ? null : o.correctChoice > i ? o.correctChoice - 1 : o.correctChoice,
                  })
                }
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
        {choices.length < 4 ? (
          <button type="button" className="btn btn-sm" onClick={() => onChange({ choices: [...choices, ""] })} data-testid="offer-setup-add-answer">
            Add an answer
          </button>
        ) : null}
        {e.choices ? <p className="small error-text" style={{ margin: 0 }}>{e.choices}</p> : null}
        {e.correctChoice ? <p className="small error-text" style={{ margin: 0 }}>{e.correctChoice.replace("correct answer", "right answer")}</p> : null}
      </fieldset>
    </div>
  );
}

interface RemoveSavedControl {
  confirm: boolean;
  busy: boolean;
  onAsk(open: boolean): void;
  onRemove(): void;
}

/** "Remove this offer?" with what it means. */
function RemoveConfirm({ control }: { control: RemoveSavedControl }) {
  return (
    <div className="notice notice-warning small os-remove-confirm" role="alert" data-testid="offer-setup-remove-confirm">
      <span>{REMOVE_OFFER_CONFIRM}</span>
      <div className="row" style={{ marginTop: 6 }}>
        <button type="button" className="btn btn-sm btn-danger" onClick={control.onRemove} disabled={control.busy} data-testid="offer-setup-remove-yes">
          {control.busy ? "Removing…" : "Remove offer"}
        </button>
        <button type="button" className="btn btn-sm" onClick={() => control.onAsk(false)} disabled={control.busy}>
          Keep it
        </button>
      </div>
    </div>
  );
}

function PartnerOfferNote({ offer, removeSaved, on }: { offer: CampaignOfferView; removeSaved: RemoveSavedControl | null; on: boolean }) {
  return (
    <section className="ss-card-section os-partner" data-testid="offer-setup-partner" data-stopped={on ? "false" : "true"}>
      <div className="os-offer-head">
        <h3 className="ss-h">{offer.current.terms.title}</h3>
        {removeSaved && !removeSaved.confirm ? (
          <button type="button" className="btn btn-sm btn-ghost os-remove" onClick={() => removeSaved.onAsk(true)} data-testid="offer-setup-remove">
            Remove this offer
          </button>
        ) : null}
      </div>
      <p className="small" style={{ margin: 0 }} data-testid="offer-setup-partner-note">
        {earlierPartnerOfferNote(on, "store_screen")}
      </p>
      {removeSaved?.confirm ? <RemoveConfirm control={removeSaved} /> : null}
    </section>
  );
}

/** The live summary: what visitors see, that it is free, the visitor limits, and what happens next. */
function OffersSummary({ view, form, validation: v }: { view: StoreOffersView; form: OffersForm; validation: OffersValidation }) {
  const steps = nextSteps(view, v);
  return (
    <div className="os-summary" data-testid="offer-setup-summary">
      <div className="ss-aside-head">
        <span className="ss-h">Summary</span>
        <span className="hint">What visitors see in your store's panel and inside your store.</span>
      </div>
      <ul className="os-summary-list">
        {form.offers.map((o) => {
          const coins = Number(o.rewardCoins);
          return (
            <li key={o.key} className="os-summary-offer" data-testid="offer-setup-summary-offer">
              <span className="os-summary-title">{o.title.trim() || "Untitled offer"}</span>
              {Number.isSafeInteger(coins) && coins > 0 ? <span className="badge badge-coin">+{formatCoins(coins)}</span> : null}
              <span className="small muted">{o.kind === "website_visit" ? "Visit your website · free for you" : "Answer a question · free for you"}</span>
            </li>
          );
        })}
      </ul>
      <p className="small" style={{ margin: 0 }} data-testid="offer-setup-free">
        <strong>{FREE_OFFERS_LINE}</strong>
      </p>
      <p className="small muted" style={{ margin: 0 }} data-testid="offer-setup-no-store-limit">
        {NO_STORE_LIMIT_LINE}
      </p>
      <ol className="os-steps small">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      <p className="hint" style={{ margin: 0 }}>
        Visitors see that this is an offer from your store and that Townsy gives the coins. You can't earn coins from your own offers. Rewarded visitors may be less interested than ordinary ones; your statistics show them separately.
      </p>
    </div>
  );
}
